import gsap from 'gsap/all'
import SplitText from 'gsap/SplitText'
import Component from '../classes/Component'
import DetectionManager from '../classes/Detection'
import { mapEach } from '../utils/dom'
import { scrollTo, scrollToInner, scrollTop } from '../utils/scroll'
import { wrap } from '../utils/text'

export default class extends Component {
  constructor (scrollbar, DJ) {
    super({
      classes: {},
      element: 'body',
      elements: {
        nav: '.nav',
        menu: '.menu',
        title: '.menu__content__title h2',
        shareDescription: '.menu__content__other__links__top p',
        shareLinks: '.menu__content__other__links ul li',
        downloadAnnualReport: '.menu__content__other__links__bottom__link',
        linkLines: '.menu__links__item__border-top, .menu__links__item__border-bottom',
        chapters: '.menu__links__item__chapter',
        titles: '.menu__links__item__title',
        videoContainer: '.menu__content__other figure',
        videos: '.menu__content__other figure video',
        menuButton: '.nav__menu',
        scrollContainer: '.scroll-container',
        navItems: '.menu__links__item',
        linksContainer: '.menu__content__other__links',
        navLogo: '.nav__logo',
        bodyContent: '.body-content',
        globeBlueContent: '.global-story__globe-content .blue-content',
        soundButton: '.nav__sound'
      }
    })

    this.isOpen = false
    this.isHidden = false
    this.isAnimating = false
    this.isAnimatingHovers = false
    this.isHoveringNavItem = false
    this.scrollbar = scrollbar
    this.soundOn = true
    this.DJ = DJ

    this.VIDEO_HOVER_TRANSITION_DURATION = 0.4

    this.create()
  }

  create () {
    this.createAnimation()
    this.elements.soundButton.addEventListener('click', () => {
      if (this.soundOn) {
        this.elements.soundButton.classList.add('active')
        this.soundOn = false
        if (this.DJ) {
          this.DJ.mute()
        }
      } else {
        this.elements.soundButton.classList.remove('active')
        this.soundOn = true
        this.DJ.unmute()
      }
    })
    this.elements.menuButton.addEventListener('click', () => {
      this.toggleMenu()
    })
    this.elements.navLogo.addEventListener('click', e => {
      e.preventDefault()
      if (this.isOpen) {
        this.closeMenu(true)
        scrollTop(this.scrollbar)
        const navOptions = {
          color: 'white',
          background: 'rgb(0, 66, 191)',
          borderColor: 'white'
        }
        gsap.set(this.elements.nav, {
          color: navOptions.color,
          background: navOptions.background,
          borderColor: navOptions.borderColor
        })
        // Reset globe mask if navigating anywhere above the globe
        gsap.set(this.elements.globeBlueContent, {
          clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0% 100%)'
        })
      } else {
        gsap.timeline()
          .to(this.elements.bodyContent, {
            opacity: 0,
            duration: 0.4,
            ease: 'power2.in',
            onComplete: () => {
              scrollTop(this.scrollbar)
              const navOptions = {
                color: 'white',
                background: 'rgb(0, 66, 191)',
                borderColor: 'white'
              }
              gsap.set(this.elements.nav, {
                color: navOptions.color,
                background: navOptions.background,
                borderColor: navOptions.borderColor
              })
              // Reset globe mask if navigating anywhere above the globe
              gsap.set(this.elements.globeBlueContent, {
                clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0% 100%)'
              })
            }
          })
          .to(this.elements.bodyContent, {
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out'
          })
      }
    })
    mapEach(this.elements.navItems, navItem => {
      navItem.addEventListener('click', () => {
        const navOptions = {
          color: 'rgb(0, 66, 191)',
          background: 'white',
          borderColor: 'rgb(0, 66, 191)'
        }
        const index = Number(navItem.dataset.index)
        // 2021 Impact
        if (index === 0) {
          scrollToInner(2.2548, this.scrollbar)
          navOptions.color = 'white'
          navOptions.background = 'rgb(0, 66, 191)'
          navOptions.borderColor = 'white'
          // Reset globe mask if navigating anywhere above the globe
          gsap.set(this.elements.globeBlueContent, {
            clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0% 100%)'
          })
        // Global Impact
        } else if (index === 1) {
          // Reset globe mask if navigating anywhere above the globe
          gsap.set(this.elements.globeBlueContent, {
            clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0% 100%)'
          })
          scrollToInner(9.296, this.scrollbar)
        // Our Distinctives
        } else if (index === 2) {
          scrollToInner(11.867, this.scrollbar)
          navOptions.color = 'black'
          navOptions.background = 'white'
          navOptions.borderColor = 'black'
        // Our Approach
        } else if (index === 3) {
          scrollTo(document.querySelector('#our-approach'), this.scrollbar)
          navOptions.color = 'black'
          navOptions.background = 'white'
          navOptions.borderColor = 'black'
        } else if (index === 4) {
          scrollTo(document.querySelector('#our-programs'), this.scrollbar)
          navOptions.color = 'black'
          navOptions.background = 'white'
          navOptions.borderColor = 'black'
        } else if (index === 5) {
          scrollToInner(999999, this.scrollbar)
        }
        this.closeMenu(true)
        gsap.set(this.elements.nav, {
          color: navOptions.color,
          background: navOptions.background,
          borderColor: navOptions.borderColor
        })
      })
    })
  }

  createAnimation () {
    this.shareDescriptionWords = new SplitText(this.elements.shareDescription, {
      type: 'words',
      wordsClass: 'word'
    })
    this.shareDescriptionWords.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })
    this.downloadAnnualReportWords = new SplitText(this.elements.downloadAnnualReport, {
      type: 'words',
      wordsClass: 'word'
    })
    this.downloadAnnualReportWords.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })
    this.resetElements()
  }

  resetElements () {
    gsap.set(this.elements.linkLines, {
      width: 0
    })
    gsap.set([
      this.elements.titles,
      this.elements.shareLinks
    ], {
      opacity: 0
    })
    gsap.set(this.elements.menu, {
      opacity: 0,
      scale: 1.5
    })
    gsap.set([
      this.shareDescriptionWords.words, this.downloadAnnualReportWords.words], {
      y: '100%'
    })
  }

  openMenu () {
    if (this.isAnimating) {
      return
    }
    this.elements.menuButton.classList.add('active')
    this.elements.menu.classList.add('open')
    this.isAnimating = true

    this.previousNavColors = {
      border: this.elements.nav.style.borderColor,
      background: this.elements.nav.style.background,
      color: this.elements.nav.style.color
    }
    this.resetElements()
    const tl = gsap.timeline({
      paused: true,
      onComplete: () => {
        this.isOpen = true
        this.isAnimating = false
      }
    })
      .to(this.elements.nav, {
        color: 'rgb(0, 66, 191)',
        background: 'white',
        borderColor: 'rgb(0, 66, 191)',
        ease: 'expo.in',
        duration: 0.75
      }, 0)
      .to(this.elements.menu, {
        scale: 1,
        opacity: 1,
        duration: 0.75,
        ease: 'expo.out'
      }, 0.75)
      .to(this.shareDescriptionWords.words, {
        y: 0,
        duration: 1.5,
        stagger: 0.05,
        ease: 'expo.out'
      }, 0.75)
      .to(this.elements.shareLinks, {
        opacity: 1,
        duration: 1.5,
        stagger: 0.05,
        ease: 'expo.out'
      }, 0.75)
      .to(this.downloadAnnualReportWords.words, {
        y: 0,
        duration: 1.5,
        stagger: 0.05,
        ease: 'expo.out'
      }, 0.75)
      .to(this.elements.linkLines, {
        width: '100%',
        duration: 2,
        ease: 'power3.inOut',
        stagger: 0.07
      }, 0.75)
      .to([this.elements.titles], {
        opacity: 1,
        duration: 1,
        stagger: 0.07,
        ease: 'expo.in'
      }, 0.75)

    if (DetectionManager.isMobileOrTablet()) {
      tl.to(this.elements.bodyContent, {
        opacity: 0,
        ease: 'expo.in',
        duration: 0.75
      }, 0)
    } else {
      tl.to(this.elements.scrollContainer, {
        opacity: 0,
        scale: 0.7,
        ease: 'expo.in',
        duration: 0.75
      }, 0)
    }
    tl.timeScale(1.5).play()
  }

  closeMenu (selfUpdateNav) {
    if (this.isAnimating) {
      return
    }
    this.elements.menuButton.classList.remove('active')
    this.elements.menu.classList.remove('open')
    this.isAnimating = true
    const tl = gsap.timeline({
      paused: true,
      onComplete: () => {
        this.isAnimating = false
        this.isOpen = false
      }
    })
      .to(this.elements.menu, {
        scale: 1.5,
        opacity: 0,
        duration: 0.5,
        ease: 'expo.in'
      }, 0)

    if (!selfUpdateNav) {
      gsap.to(this.elements.nav, {
        color: this.previousNavColors.color,
        borderColor: this.previousNavColors.border,
        background: this.previousNavColors.background,
        duration: 0.5,
        ease: 'power3.out'
      }, 0)
    }

    if (DetectionManager.isMobileOrTablet()) {
      tl.to(this.elements.bodyContent, {
        opacity: 1,
        duration: 0.5,
        ease: 'power3.out'
      }, 0.5)
    } else {
      tl.to(this.elements.scrollContainer, {
        scale: 1,
        opacity: 1,
        duration: 0.5,
        ease: 'power3.out'
      }, 0.5)
    }
    tl.timeScale(1.75).play()
  }

  toggleMenu () {
    if (this.isAnimating) {
      return
    }
    if (this.isOpen) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  onResize () {

  }

  update () {
    if (!Array.from(this.elements.navItems).some(item => {
      const backgroundColor = window.getComputedStyle(item).getPropertyValue('background-color')
      return backgroundColor === 'rgb(255, 255, 255)'
    })) {
      if (!this.isHoveringNavItem) {
        return
      }
      this.isHoveringNavItem = false
      const tl = gsap.timeline({
        paused: true
      })
      tl.to([this.elements.navItems, this.elements.linksContainer, this.elements.title], {
        opacity: 1,
        duration: 0.3,
        ease: 'none'
      }, 0)
      this.elements.videos.forEach((video, index) => {
        if (index === 0) {
          tl.to(video, {
            scale: 1,
            opacity: 1,
            duration: 0.3,
            ease: 'none'
          }, 0)
        } else {
          tl.to(video, {
            scale: 1.1,
            opacity: 0,
            duration: 0.3,
            ease: 'none'
          }, 0)
        }
      })
      tl.play()
    } else {
      this.elements.navItems.forEach((item, index) => {
        if (this.isAnimatingHovers) {
          return
        }
        const backgroundColor = window.getComputedStyle(item).getPropertyValue('background-color')
        if (backgroundColor === 'rgb(255, 255, 255)') {
          this.isHoveringNavItem = true
          this.isAnimatingHovers = true
          const index = parseInt(item.dataset.index)
          const tl = gsap.timeline({
            paused: true,
            onComplete: () => {
              this.isAnimatingHovers = false
            }
          })
            .to([this.elements.linksContainer, this.elements.title], {
              opacity: 0.5,
              duration: 0.3,
              ease: 'none'
            }, 0)
          this.elements.videos.forEach((innerItem, innerIndex) => {
            if (innerIndex === index + 1) {
              tl.to(innerItem, {
                opacity: 1,
                scale: 1,
                duration: 0.3,
                ease: 'none'
              }, 0)
            } else {
              tl.to(innerItem, {
                opacity: 0,
                scale: 1.1,
                duration: 0.3,
                ease: 'none'
              }, 0)
            }
          })
          this.elements.navItems.forEach((innerItem, innerIndex) => {
            tl.to(innerItem, {
              opacity: innerIndex === index ? 1 : 0.5,
              duration: 0.3,
              ease: 'none'
            }, 0)
          })
          tl.play()
        }
      })
    }
  }
}
